import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api.js";
import type { CollectionItem, DashboardData, Manuscript } from "../lib/types.js";
import type { SidebarSection } from "./Sidebar.js";
import { Icon, type IconName } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { CharacterAvatar } from "../components/CharacterAvatar.js";
import { formatNumber, formatRelativeTime, statusLabel } from "../lib/format.js";
import { WritingGoalCard } from "./WritingGoalCard.js";

interface Props {
  projectId: string;
  reloadKey?: number;
  onOpenScene: (sceneId: string) => void;
  onOpenManuscript: () => void;
  onOpenSection: (section: SidebarSection) => void;
  onExport: () => void;
  onSettings: () => void;
  onProjectChanged?: () => void;
}

const str = (v: unknown) => (typeof v === "string" ? v : "");

/** Downscale an image file to a wide banner JPEG data URL (long edge ~1600px). */
async function fileToBanner(file: File): Promise<string> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(new Error("Could not read the image."));
    r.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = () => reject(new Error("That file is not a readable image."));
    el.src = dataUrl;
  });
  const MAX = 1600;
  let { width, height } = img;
  if (width > MAX || height > MAX) { const s = MAX / Math.max(width, height); width = Math.round(width * s); height = Math.round(height * s); }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return dataUrl;
  ctx.drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", 0.82);
}

export function OverviewPanel({ projectId, reloadKey, onOpenScene, onOpenManuscript, onOpenSection, onExport, onSettings, onProjectChanged }: Props) {
  const toast = useToast();
  const [data, setData] = useState<DashboardData | null>(null);
  const [manuscript, setManuscript] = useState<Manuscript | null>(null);
  const [characters, setCharacters] = useState<CollectionItem[]>([]);
  const [counts, setCounts] = useState({ characters: 0, relationships: 0, locations: 0, plotThreads: 0 });
  const [banner, setBanner] = useState<string | null>(null);
  const [showMore, setShowMore] = useState(false);
  const [canExpand, setCanExpand] = useState(false);
  const bannerInput = useRef<HTMLInputElement>(null);
  const descRef = useRef<HTMLParagraphElement>(null);

  const load = useCallback(async () => {
    try {
      const [d, m, chars, locs, threads, rels] = await Promise.all([
        api.dashboard(projectId),
        api.manuscript(projectId),
        api.listCollection(projectId, "characters"),
        api.listCollection(projectId, "locations"),
        api.listCollection(projectId, "plot-threads"),
        api.relationships(projectId)
      ]);
      setData(d);
      setManuscript(m);
      setCharacters(chars.items);
      setBanner(d.project.bannerImage ?? null);
      const relPairs = new Set((rels.events ?? []).filter((e) => e.approved).map((e) => `${[e.fromId, e.toId].sort().join("|")}::${e.kind}`));
      setCounts({ characters: chars.items.length, relationships: relPairs.size, locations: locs.items.length, plotThreads: threads.items.length });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to load overview");
    }
  }, [projectId, toast]);
  useEffect(() => { void load(); }, [load, reloadKey]);

  // Detect whether the synopsis is clamped (overflows 3 lines) so "Show more" only appears when useful.
  useEffect(() => {
    const el = descRef.current;
    if (el && !showMore) setCanExpand(el.scrollHeight - el.clientHeight > 4);
  }, [data, showMore]);

  const pickBanner = async (file: File | null) => {
    if (!file) return;
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { toast.error("Use a JPG, PNG, or WebP image."); return; }
    try {
      const dataUrl = await fileToBanner(file);
      setBanner(dataUrl);
      await api.updateProject(projectId, { bannerImage: dataUrl });
      toast.success("Banner updated.");
      onProjectChanged?.();
    } catch (err) { toast.error(err instanceof Error ? err.message : "Couldn't set banner"); }
  };
  const removeBanner = async () => {
    setBanner(null);
    try { await api.updateProject(projectId, { bannerImage: null }); onProjectChanged?.(); } catch { /* ignore */ }
  };

  if (!data) return <div className="overview-loading"><Icon name="book" size={24} className="spin" style={{ color: "var(--accent)" }} /></div>;
  const { project, stats } = data;

  const chapterWords = manuscript ? [...manuscript.chapters].sort((a, b) => a.position - b.position).map((c) => c.wordCount) : [];
  const sceneWords = manuscript ? [...manuscript.scenes].sort((a, b) => a.position - b.position).map((s) => s.wordCount) : [];
  const avgChapter = stats.chapterCount ? Math.round(stats.wordCount / stats.chapterCount) : 0;
  const avgScene = stats.sceneCount ? Math.round(stats.wordCount / stats.sceneCount) : 0;
  const goalPct = stats.targetWordCount ? Math.min(100, Math.round((stats.wordCount / stats.targetWordCount) * 100)) : 0;

  const portraitPeople = [...characters].sort((a, b) => (b.imageData ? 1 : 0) - (a.imageData ? 1 : 0)).slice(0, 6);

  const quick = async (fn: () => Promise<void>) => { try { await fn(); } catch (err) { toast.error(err instanceof Error ? err.message : "Action failed"); } };
  const newChapter = () => quick(async () => {
    const { chapter } = await api.createChapter(projectId, `Chapter ${stats.chapterCount + 1}`, null);
    const { scene } = await api.createScene(chapter.id, "Scene 1");
    onOpenScene(scene.id);
  });
  const newEntity = (path: string, body: Record<string, unknown>, section: SidebarSection) =>
    quick(async () => { await api.createCollectionItem(projectId, path, body); onOpenSection(section); });

  return (
    <div className="ov">
      {/* Hero banner */}
      <div className="ov-hero" style={banner ? { backgroundImage: `url(${banner})` } : undefined}>
        <div className="ov-hero-shade" />
        <div className="ov-hero-top">
          <div className="ov-crumb"><Icon name="home" size={12} /> Projects <Icon name="chevronRight" size={11} /> {project.title}</div>
          <div className="row gap-2">
            <button className="btn btn-sm btn-ghost ov-glass" onClick={() => bannerInput.current?.click()}><Icon name="image" size={13} /> {banner ? "Change banner" : "Add banner"}</button>
            {banner && <button className="btn btn-sm btn-ghost ov-glass" onClick={() => void removeBanner()} title="Remove banner"><Icon name="x" size={13} /></button>}
            <button className="btn btn-sm btn-ghost ov-glass" onClick={onSettings}><Icon name="settings" size={13} /> Settings</button>
          </div>
        </div>
        <div className="ov-hero-body">
          <div className="row gap-2" style={{ flexWrap: "wrap", marginBottom: 10 }}>
            <span className="chip chip-accent">{statusLabel(project.status)}</span>
            {project.genre && <span className="chip ov-glass">{project.genre}</span>}
            {project.seriesName && <span className="chip ov-glass">{project.seriesName}{project.seriesNumber != null ? ` · #${project.seriesNumber}` : ""}</span>}
          </div>
          <h1 className="ov-title">{project.title}</h1>
          {project.seriesName && project.volumeTitle && (
            <p className="ov-volume">Volume{project.seriesNumber != null ? ` ${project.seriesNumber}` : ""}: <em>{project.volumeTitle}</em></p>
          )}
          {project.author && <p className="ov-author">by <em>{project.author}</em></p>}
          {project.description && (
            <>
              <p ref={descRef} className={`ov-desc${showMore ? " expanded" : ""}`}>{project.description}</p>
              {canExpand && (
                <button className="ov-more" onClick={() => setShowMore((s) => !s)}>
                  {showMore ? "Show less" : "Show more"}<Icon name={showMore ? "chevronUp" : "chevronDown"} size={12} />
                </button>
              )}
            </>
          )}
          <div className="ov-cta">
            {data.continueScene ? (
              <button className="btn btn-primary" onClick={() => onOpenScene(data.continueScene!.id)}>
                <Icon name="edit" size={15} /> Continue writing <span className="faint" style={{ fontWeight: 500 }}>· {data.continueScene.chapterTitle} / {data.continueScene.title}</span>
              </button>
            ) : (
              <button className="btn btn-primary" onClick={onOpenManuscript}><Icon name="edit" size={15} /> Open manuscript</button>
            )}
            <button className="btn ov-glass" onClick={onOpenManuscript}><Icon name="layers" size={15} /> Manuscript</button>
            <button className="btn ov-glass" onClick={onExport}><Icon name="download" size={15} /> Export</button>
            <button className="btn ov-glass" onClick={() => window.open(`/read/${projectId}`, "_blank", "noopener")}><Icon name="book" size={15} /> Open E-Book</button>
          </div>
        </div>
        <input ref={bannerInput} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }} onChange={(e) => { void pickBanner(e.target.files?.[0] ?? null); e.target.value = ""; }} />
      </div>

      <div className="ov-columns">
        <div className="ov-main">
          <div className="ov-stats">
            <div className="card ov-stat">
              <div className="ov-stat-head"><Icon name="file" size={16} style={{ color: "var(--accent)" }} /> Words</div>
              <div className="ov-stat-num">{formatNumber(stats.wordCount)}</div>
              {stats.targetWordCount ? (
                <>
                  <div className="ov-stat-bar"><div style={{ width: `${goalPct}%` }} /></div>
                  <div className="ov-stat-sub">{goalPct}% of {formatNumber(stats.targetWordCount)} · {formatNumber(Math.max(0, stats.targetWordCount - stats.wordCount))} to go</div>
                </>
              ) : <button className="btn btn-sm btn-ghost" style={{ marginTop: 8, alignSelf: "flex-start" }} onClick={onSettings}><Icon name="target" size={12} /> Set a goal</button>}
            </div>
            <div className="card ov-stat">
              <div className="ov-stat-head"><Icon name="layers" size={16} style={{ color: "var(--accent)" }} /> Chapters</div>
              <div className="ov-stat-row2"><span className="ov-stat-num">{formatNumber(stats.chapterCount)}</span><MiniBars values={chapterWords} unit="chapter" /></div>
              <div className="ov-stat-sub">Avg {formatNumber(avgChapter)} words each</div>
            </div>
            <div className="card ov-stat">
              <div className="ov-stat-head"><Icon name="book" size={16} style={{ color: "var(--accent)" }} /> Scenes</div>
              <div className="ov-stat-row2"><span className="ov-stat-num">{formatNumber(stats.sceneCount)}</span><MiniBars values={sceneWords} unit="scene" /></div>
              <div className="ov-stat-sub">Avg {formatNumber(avgScene)} words each</div>
            </div>
          </div>

          <WritingGoalCard projectId={projectId} reloadKey={reloadKey} />

          <section className="card pad">
            <div className="row gap-2" style={{ marginBottom: 14 }}>
              <Icon name="users" size={17} style={{ color: "var(--accent)" }} /><h3>Story elements</h3>
              <span className="spacer" style={{ flex: 1 }} />
            </div>
            <div className="ov-elements">
              <ElementCard icon="users" label="Characters" count={counts.characters} unit="in the cast" people={portraitPeople} onOpen={() => onOpenSection("characters")} />
              <ElementCard icon="heart" label="Relationships" count={counts.relationships} unit="tracked connections" people={portraitPeople} onOpen={() => onOpenSection("relationships")} />
              <ElementCard icon="map" label="Locations" count={counts.locations} unit="unique settings" onOpen={() => onOpenSection("locations")} />
              <ElementCard icon="thread" label="Plot threads" count={counts.plotThreads} unit="story threads" onOpen={() => onOpenSection("plotThreads")} />
            </div>
          </section>
        </div>

        <div className="ov-side">
          <section className="card pad">
            <div className="ov-side-head"><Icon name="book" size={16} style={{ color: "var(--accent)" }} /><h3>Current chapter</h3><span className="spacer" style={{ flex: 1 }} /><button className="btn btn-icon btn-ghost btn-sm" onClick={onOpenManuscript}><Icon name="chevronRight" size={15} /></button></div>
            {data.continueScene ? (
              <button className="ov-current" onClick={() => onOpenScene(data.continueScene!.id)}>
                <div className="ov-current-body">
                  <div className="ov-current-chapter">{data.continueScene.chapterTitle}</div>
                  <div className="ov-current-scene truncate">{data.continueScene.title}</div>
                  <div className="faint" style={{ fontSize: 11.5, marginTop: 3 }}>Pick up where you left off</div>
                </div>
                <Icon name="chevronRight" size={16} className="ov-chev" />
              </button>
            ) : <p className="muted" style={{ margin: 0, fontSize: 13 }}>Start writing to see your current chapter here.</p>}
          </section>

          <section className="card pad">
            <div className="ov-side-head"><Icon name="history" size={16} style={{ color: "var(--accent)" }} /><h3>Recent edits</h3><span className="spacer" style={{ flex: 1 }} /><button className="btn btn-sm btn-ghost" onClick={onOpenManuscript}>View all</button></div>
            {data.recentActivity.length === 0 ? (
              <p className="muted" style={{ margin: 0, fontSize: 13 }}>Your saves and revisions will show here.</p>
            ) : (
              <ul className="ov-edits">
                {data.recentActivity.slice(0, 6).map((a, i) => (
                  <li key={i} onClick={() => onOpenScene(a.sceneId)}>
                    <span className="ov-edit-ico"><Icon name="edit" size={13} /></span>
                    <div className="grow" style={{ minWidth: 0 }}>
                      <div className="truncate" style={{ fontSize: 12.5 }}><span className="strong">{a.sceneTitle}</span></div>
                      <div className="truncate faint" style={{ fontSize: 11 }}>{a.chapterTitle}</div>
                    </div>
                    <span className="faint" style={{ fontSize: 11, flex: "none" }}>{formatRelativeTime(a.createdAt)}</span>
                  </li>
                ))}
              </ul>
            )}
          </section>

          <section className="card pad">
            <div className="ov-side-head"><Icon name="sparkles" size={16} style={{ color: "var(--accent)" }} /><h3>Quick actions</h3></div>
            <div className="ov-quick">
              <button className="btn btn-sm" onClick={newChapter}><Icon name="plus" size={13} /> New chapter</button>
              <button className="btn btn-sm" onClick={() => newEntity("characters", { name: "New Character" }, "characters")}><Icon name="plus" size={13} /> New character</button>
              <button className="btn btn-sm" onClick={() => newEntity("locations", { name: "New Location" }, "locations")}><Icon name="plus" size={13} /> New location</button>
              <button className="btn btn-sm" onClick={() => newEntity("notes", { title: "New Note" }, "notes")}><Icon name="plus" size={13} /> New note</button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

function MiniBars({ values, unit }: { values: number[]; unit: string }) {
  if (values.length === 0) return null;
  const max = Math.max(...values, 1);
  const start = Math.max(0, values.length - 16);
  const bars = values.slice(-16);
  return (
    <span className="ov-spark">
      {bars.map((v, i) => (
        <span
          key={i}
          style={{ height: `${Math.max(8, Math.round((v / max) * 100))}%` }}
          title={`${unit.charAt(0).toUpperCase()}${unit.slice(1)} ${start + i + 1}: ${formatNumber(v)} words`}
        />
      ))}
    </span>
  );
}

function ElementCard({ icon, label, count, unit, people, onOpen }: {
  icon: IconName; label: string; count: number; unit: string;
  people?: CollectionItem[]; onOpen: () => void;
}) {
  const withPeople = people?.filter((p) => p.imageData) ?? [];
  return (
    <button className="ov-element" onClick={onOpen}>
      <div className="ov-element-head"><Icon name={icon} size={14} style={{ color: "var(--accent)" }} /> {label} <Icon name="chevronRight" size={13} className="faint" /></div>
      <div className="ov-element-count">{count}</div>
      <div className="faint" style={{ fontSize: 11 }}>{unit}</div>
      {people && people.length > 0 && (
        <div className="ov-element-people">
          {(withPeople.length > 0 ? withPeople : people).slice(0, 5).map((p) => (
            <CharacterAvatar key={p.id} name={str(p.name) || "?"} imageUrl={p.imageData as string | null | undefined} size="xs" />
          ))}
        </div>
      )}
    </button>
  );
}
